import { db } from './firebase';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';

export interface Testimonial {
  id: string;
  clientName: string;
  projectName: string;
  comment: string;
  rating: number;
  initials: string;
  order: number;
  active: boolean;
}

class TestimonialService {
  private readonly COLLECTION = 'testimonials';
  
  // Get all testimonials, sorted by order
  async getAllTestimonials(): Promise<Testimonial[]> {
    try {
      const q = query(collection(db, this.COLLECTION), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Testimonial[];
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      throw error;
    }
  }
  
  // Get only active testimonials for the public site
  async getActiveTestimonials(): Promise<Testimonial[]> {
    try {
      const testimonials = await this.getAllTestimonials();
      return testimonials.filter(testimonial => testimonial.active);
    } catch (error) {
      console.error('Error fetching active testimonials:', error);
      throw error;
    }
  }
  
  // Get a single testimonial by ID
  async getTestimonial(id: string): Promise<Testimonial | null> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Testimonial;
    } catch (error) {
      console.error(`Error fetching testimonial with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Add a new testimonial
  async addTestimonial(testimonial: Omit<Testimonial, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.COLLECTION), testimonial);
      return docRef.id;
    } catch (error) {
      console.error('Error adding testimonial:', error);
      throw error;
    }
  }
  
  // Update an existing testimonial
  async updateTestimonial(id: string, updates: Partial<Omit<Testimonial, 'id'>>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error(`Error updating testimonial with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Delete a testimonial
  async deleteTestimonial(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting testimonial with ID ${id}:`, error);
      throw error;
    }
  }
  
  // Reorder testimonials
  async updateTestimonialOrder(orderedIds: string[]): Promise<void> {
    try {
      // Update each testimonial with its new order
      const batch = Promise.all(
        orderedIds.map((id, index) => 
          this.updateTestimonial(id, { order: index })
        )
      );
      
      await batch;
    } catch (error) {
      console.error('Error updating testimonial order:', error);
      throw error;
    }
  }
}

export const testimonialService = new TestimonialService(); 